import { CustomClaimValue, Healths, useAuth } from "@app/shared";
import { format, formatDistanceToNowStrict } from "date-fns";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ReactComponent as Logo } from "../../assets/images/Omnisight-White-O.svg";
import { useFetch } from "../../hooks";
import { Pages } from "../pages";
import { Icons, ProfileIcon } from "../shared";
import { HoverTooltip } from "../shared/HoverTooltip";
import { SidebarIcon } from "./SidebarIcon";

/**
 * Resident memory as MB.
 *
 * MB rather than a scaled unit on purpose: these instances get OOM-killed somewhere north of 600MB
 * on a ~1GiB box, so a number you can compare against that ceiling at a glance beats one that
 * silently switches to GB. Binary MiB to match what `free`, CloudWatch and the kernel report.
 */
function formatWorkingSet(bytes: number | undefined): string {
    if (bytes === undefined || bytes === null) return "unknown";
    return `${Math.round(bytes / (1024 * 1024)).toLocaleString()} MB`;
}

export interface SidebarNavRef {
    openMenu: () => void;
    closeMenu: () => void;
}

export const SidebarNav = forwardRef<SidebarNavRef>((props, ref) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [versionInfo, ,] = useFetch(() => Healths.getStatus(), []);
    const user = useAuth();
    const isSuperAdmin = user.userClaims?.find(uc => uc.claims.find(c => c === CustomClaimValue.SuperAdmin));
    // had to expose methods to parent to handle close and open menu from the burguer button.
    useImperativeHandle(ref, () => ({
        openMenu: () => setIsMobileMenuOpen(true),
        closeMenu: () => setIsMobileMenuOpen(false),
    }));

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const handleRouteChange = () => {
            setIsMobileMenuOpen(false);
        };
        window.addEventListener("popstate", handleRouteChange);
        return () => window.removeEventListener("popstate", handleRouteChange);
    }, []);


    return (
        <>
            {isMobileMenuOpen && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setIsMobileMenuOpen(false)} />}

            <div
                className={`
                w-64 md:w-16 bg-background-left-nav min-h-full h-full flex flex-col items-center space-y-2 overflow-y-auto overflow-x-hidden min-w-fit
                fixed md:relative z-50
                transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
            `}
            >
                {/* The phone drawer is wide enough for labels (SidebarIcon prints them below md), so
                    its head is a row: logo left, close button right. From md up the rail is 64px and
                    the head stacks as before. */}
                <div className="w-full flex flex-row items-center justify-between px-3 pt-2 gap-3 md:flex-col md:px-2">
                    {!isSuperAdmin && <a href="/" className="flex-shrink-0">
                        <Logo height={36} className="fill-nav-logo-tint"></Logo>
                    </a>}
                    {isSuperAdmin && <HoverTooltip offset={[50, 10]} placement="bottom-start" hoverable={
                        <a href="/">
                            <Logo height={36} className="fill-nav-logo-tint"></Logo>
                        </a>}>
                        <ul>
                            <li>Version: {versionInfo?.version}</li>
                            <li>Branch: {versionInfo?.branch}</li>
                            <li>Uncommitted Changes: {(versionInfo?.hasUncommittedChanges) ? "true" : "false"}</li>
                            <li>Last Commit: {versionInfo?.lastCommitSHA}</li>
                            <li>Date: {versionInfo?.versionDate ? format(versionInfo.versionDate, "Pp zzz") : "unknown"}</li>
                            <li>Started: {versionInfo?.startedAtUtc ? format(versionInfo.startedAtUtc, "Pp zzz") : "unknown"}</li>
                            <li>Uptime: {versionInfo?.startedAtUtc ? formatDistanceToNowStrict(versionInfo.startedAtUtc) : "unknown"}</li>
                                                        {/* Which box answered — CoreApi is behind a shared ALB, and this is the same
                                value the logs carry as MachineName. */}
                            <li>Instance: {versionInfo?.machineName ?? "unknown"}</li>
                            <li>Memory: {formatWorkingSet(versionInfo?.workingSetBytes)}</li>
                        </ul>
                    </HoverTooltip>}
                    <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden bg-white/10 hover:bg-white/20 text-white/80 hover:text-white p-2 rounded-lg transition-all duration-200 w-12 h-12 flex items-center justify-center border border-white/10 hover:border-white/30" aria-label="Close menu">
                        <Icons.Times size={24} />
                    </button>
                </div>
                {/* <div className="h-px w-12 bg-link-left-nav" /> */}
                <ul className="list-none w-full space-y-7 pt-6 flex-grow border-t">
                    <SidebarIcon hover="Location Groups" to={Pages.ViewFacilityGroups.getUrl()} Icon={Icons.LayerGroup} />
                    <SidebarIcon hover="Organization Events" to={Pages.OrganizationEvents.getUrl()} Icon={Icons.Calendar} />
                    <SidebarIcon hover="Devices" to={Pages.Devices.getUrl()} Icon={Icons.NavRadar} />
                    <SidebarIcon hover="Users" to={Pages.Users.getUrl()} Icon={Icons.Users} claim={CustomClaimValue.ManageUsers} />
                    <SidebarIcon hover="Integrations" to={Pages.ClientEndpoints.getUrl()} Icon={Icons.Api} />
                    {isSuperAdmin && <SidebarIcon hover="Notifications" to={Pages.NotificationSettings.getUrl()} Icon={Icons.AlertTriangle} />}
                    {isSuperAdmin && <SidebarIcon hover="Site Admin" to={Pages.Settings.getUrl()} Icon={Icons.NavSettings} />}
                    {isSuperAdmin && <SidebarIcon hover="Device Labels" to={Pages.DeviceLabels.getUrl()} Icon={Icons.Print} />}
                    {isSuperAdmin && <SidebarIcon hover="Sales Planner" to={Pages.SalesLayoutPlanner.getUrl()} Icon={Icons.DollarSign} />}
                    {isSuperAdmin && <SidebarIcon hover="Visualizer" to={Pages.Visualizer.getUrl()} Icon={Icons.Eye} />}
                </ul>
                <ul className="list-none w-full space-y-7 py-6 border-t ">
                    <SidebarIcon hover="Profile">
                        <ProfileIcon className="w-full flex justify-center" />
                    </SidebarIcon>
                </ul>
            </div>
        </>
    );
});
